import type { Cell, Level } from "./types";

/** Circular reach of a noria, in grid cells (Euclidean). */
export const RADIUS = 2.5;

/**
 * The five oases (the "difficulty pass" generation). The geometry was designed
 * with a seeded cluster search and curated by hand; every level's `opt` is the
 * **proven** maximum coverage from a real HiGHS MCLP solve (the same model the
 * reveal solves live), re-asserted in `levels.test.ts` on every test run.
 *
 * Difficulty design, also guarded by the test:
 * - `opt < total` on every oasis — there are never enough wheels, so 3 stars
 *   means choosing which palms NOT to water.
 * - From oasis 2 on, the greedy baseline ("always drop the next noria where it
 *   waters the most still-dry palms") is provably 2–3 palms short of the
 *   optimum, so the obvious play earns 2 stars (`s2` sits at that baseline)
 *   and 3 stars (`s3 = opt`) takes real repositioning.
 *
 * Proven optima with these noria budgets: 12, 14, 18, 27, 30.
 */
export const LEVELS: Level[] = [
  {
    name: "The First Well", cols: 10, rows: 7, norias: 2,
    total: 13, s1: 7, s2: 10, s3: 12, opt: 12,
    trees: [[1, 3], [1, 4], [1, 5], [2, 3], [2, 4], [2, 5], [3, 1], [3, 2], [3, 3], [3, 4],
            [4, 1], [4, 6], [5, 8]] as Cell[],
    rocks: [[4, 3], [5, 3], [6, 3]] as Cell[],
  },
  {
    name: "Twin Groves", cols: 12, rows: 8, norias: 2,
    total: 18, s1: 8, s2: 12, s3: 14, opt: 14,
    trees: [[1, 5], [1, 7], [2, 0], [2, 2], [2, 5], [2, 6], [2, 7], [3, 1], [3, 2], [3, 5],
            [3, 6], [4, 6], [5, 2], [5, 3], [6, 2], [6, 3], [6, 8], [7, 10]] as Cell[],
    rocks: [[2, 4], [2, 9], [3, 4], [3, 9], [4, 3]] as Cell[],
  },
  {
    name: "Dry Lakebed", cols: 12, rows: 9, norias: 3,
    total: 22, s1: 10, s2: 16, s3: 18, opt: 18,
    trees: [[0, 2], [2, 4], [2, 5], [2, 8], [2, 9], [2, 11], [3, 4], [3, 9], [4, 4], [4, 5],
            [5, 3], [5, 4], [5, 8], [6, 6], [6, 7], [7, 0], [7, 2], [7, 6], [7, 7], [7, 8],
            [8, 0], [8, 7]] as Cell[],
    rocks: [[1, 6], [2, 6], [3, 5], [3, 6], [3, 7], [3, 8], [4, 6], [4, 7], [4, 8]] as Cell[],
  },
  {
    name: "Shifting Dunes", cols: 13, rows: 9, norias: 4,
    total: 28, s1: 15, s2: 24, s3: 27, opt: 27,
    trees: [[0, 2], [1, 10], [1, 11], [2, 9], [2, 10], [2, 11], [2, 12], [3, 1], [3, 2], [3, 8],
            [3, 9], [3, 10], [3, 11], [4, 1], [4, 2], [4, 9], [4, 10], [5, 5], [5, 6], [6, 3],
            [6, 4], [6, 5], [6, 6], [6, 7], [7, 1], [7, 4], [7, 5], [7, 8]] as Cell[],
    rocks: [[0, 3], [0, 10], [1, 3], [1, 9], [2, 3], [2, 8], [3, 7], [4, 7], [5, 7], [6, 8]] as Cell[],
  },
  {
    name: "The Great Palmery", cols: 13, rows: 10, norias: 4,
    total: 34, s1: 17, s2: 27, s3: 30, opt: 30,
    trees: [[1, 5], [1, 7], [1, 10], [2, 9], [2, 10], [3, 1], [3, 8], [3, 9], [3, 10], [3, 11],
            [4, 6], [4, 7], [4, 9], [5, 3], [5, 4], [5, 5], [5, 7], [5, 8], [6, 0], [6, 8],
            [6, 10], [7, 7], [7, 8], [7, 9], [7, 10], [7, 12], [8, 7], [8, 8], [8, 9], [8, 11],
            [8, 12], [9, 1], [9, 7], [9, 8]] as Cell[],
    rocks: [[4, 1], [4, 2], [5, 1], [5, 2], [6, 3]] as Cell[],
  },
];
